import {ArrowLeftOutlined, EyeInvisibleOutlined, EyeOutlined} from '@ant-design/icons';
import {CountdownTimer} from '@countdown-timer';
import {Button, Input} from '@ds/components';
import {FullScreenLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {updateShowVendorToast} from 'store/slices/dashboard/slicer';
import {getBranches} from 'store/slices/dashboard/thunk';
import {toggleAddToHomeModal} from 'store/slices/home/slicer';
import {loginUserByOtp, sendOtp} from 'store/slices/login/thunk';
import {getSelectedVendorFromStorage, mobileValidation, p2e, sanitizeRedirect} from 'utils/Helper';

import AcademyLink from './components/AcademyLink/AcademyLink';
import LoginBrand from './components/LoginBrand/LoginBrand';

const OTP_COUNTDOWN_SECONDS = 60;

const Login: React.FC = () => {
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneErrorValue, setPhoneErrorValue] = useState('');
  const [passValue, setPassValue] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [pendingForOtp, setPendingForOtp] = useState(false);
  const [otpSend, setOtpSend] = useState(false);
  const [countDown, setCountDown] = useState(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rawRedirect = new URLSearchParams(location.search).get('redirect');
  const redirectPath = sanitizeRedirect(rawRedirect ?? '', '/home');
  const {logging, auth, sendOtpError, sendingOtp} = useAppSelector((state) => state.login);

  const navigateAfterAuth = useCallback(() => {
    void dispatch(getBranches({page: 0, pageSize: 10}))
      .unwrap()
      .then((response) => {
        localStorage.setItem('isVendor', String(Boolean(response?.branches?.length)));
        dispatch(updateShowVendorToast(true));
        dispatch(toggleAddToHomeModal({show: false}));
        navigate(redirectPath, {replace: true});
      })
      .catch(() => {
        navigate(redirectPath, {replace: true});
      });
  }, [dispatch, navigate, redirectPath]);

  const handleChangePhoneValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneValue(p2e(event.target.value).trim());
  };

  const handleChangePass = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassValue(p2e(event.target.value).trim());
  };

  const handleSendOtp = () => {
    if (!mobileValidation(phoneValue)) {
      setPhoneErrorValue('مطمئنید شماره موبایل رو درست وارد کردید؟');
      return;
    }

    void dispatch(sendOtp(phoneValue))
      .unwrap()
      .then((response) => {
        if (response?.status !== 200) {
          toast('خطا در ارسال کد');
          return;
        }

        toast(response?.meta?.message || response?.message || 'کد ارسال شد');
        setPendingForOtp(true);
        setCountDown(OTP_COUNTDOWN_SECONDS);
        setOtpSend(true);
      });
  };

  const handleLogin = () => {
    if (!mobileValidation(phoneValue)) {
      setPhoneErrorValue('مطمئنید شماره موبایل رو درست وارد کردید؟');
      return;
    }

    if (!pendingForOtp && !otpSend) {
      handleSendOtp();
    } else {
      dispatch(loginUserByOtp({cellphone: phoneValue, code: passValue}));
    }

    setPhoneErrorValue('');
  };

  const handleOtpKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  useEffect(() => {
    if (sendOtpError?.message) {
      setPendingForOtp(false);
    }
  }, [sendOtpError]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
      return;
    }

    if (getSelectedVendorFromStorage()) {
      navigate(redirectPath, {replace: true});
    } else {
      navigateAfterAuth();
    }
  }, [navigate, navigateAfterAuth, redirectPath]);

  useEffect(() => {
    if (!logging && auth) {
      localStorage.setItem('access_token', auth.access_token);
      localStorage.setItem('refresh_token', auth.refresh_token);
      navigateAfterAuth();
    }
  }, [auth, logging, navigateAfterAuth]);

  return (
    <FullScreenLayout>
      <div className="flex w-full flex-1 items-center">
        <main className="flex h-full w-full flex-1 flex-col items-center justify-center">
          <div className="relative flex w-75.5 flex-2 flex-col items-center justify-center gap-6 mb-4">
            <LoginBrand />

            <div className="flex w-full flex-col gap-4">
              {otpSend && (
                <Button
                  variant="link"
                  color="secondary"
                  size="sm"
                  className="self-end px-0"
                  endIcon={<ArrowLeftOutlined aria-hidden />}
                  onClick={() => {
                    setOtpSend(false);
                    setPendingForOtp(false);
                  }}
                >
                  تغییر شماره موبایل
                </Button>
              )}

              <div className="flex flex-col items-end gap-3">
                <Input
                  disabled={otpSend}
                  name="phone"
                  required
                  type="tel"
                  placeholder="شماره موبایل خود را وارد کنید"
                  value={phoneValue}
                  onChange={handleChangePhoneValue}
                  help={phoneErrorValue}
                  status={phoneErrorValue ? 'error' : undefined}
                  placeholderAlign="rightPlaceholder"
                />

                {otpSend && (
                  <Input
                    name="password"
                    onKeyDown={handleOtpKeyDown}
                    value={passValue}
                    onChange={handleChangePass}
                    type={showPass ? 'text' : 'password'}
                    placeholder="ورود رمز یکبار مصرف"
                    onSuffixClick={() => setShowPass((visible) => !visible)}
                    suffix={
                      showPass ? (
                        <EyeInvisibleOutlined aria-hidden className="text-body-xl" />
                      ) : (
                        <EyeOutlined aria-hidden className="text-body-xl" />
                      )
                    }
                  />
                )}

                {otpSend && pendingForOtp && (
                  <div className="flex w-full items-center gap-2.5 text-body-xs text-neutral-fg">
                    {countDown > 0 ? (
                      <>
                        درخواست دوباره رمز تا
                        <CountdownTimer initialCounter={countDown} setCountDown={setCountDown} />
                      </>
                    ) : (
                      <div className="my-5 flex items-center gap-2">
                        کد را دریافت نکردید؟
                        <Button onClick={handleSendOtp} variant="link" color="secondary">
                          ارسال دوباره کد
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Button
              disabled={(!phoneValue && !otpSend) || (otpSend && !passValue)}
              fullWidth
              onClick={handleLogin}
              loading={logging || sendingOtp}
            >
              {otpSend ? 'ورود به نما' : 'ادامه'}
            </Button>
          </div>

          <div className="flex w-75.75 flex-1">
            <AcademyLink />
          </div>
        </main>
      </div>
    </FullScreenLayout>
  );
};

export default Login;
